import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building, Mail, Phone, Globe, UserCheck, Calendar,
  FileText, MoreVertical, Edit2, Trash2
} from 'lucide-react';

interface Client {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  contact_count?: number;
  is_active: boolean;
  created_at: string;
}

interface ClientCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (clientId: number) => void;
}

export default function ClientCard({ client, onEdit, onDelete }: ClientCardProps) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
              {client.company && (
                <p className="text-sm text-gray-600">{client.company}</p>
              )}
            </div>
          </div>
          <div className="relative group">
            <button className="text-gray-400 hover:text-gray-600">
              <MoreVertical className="w-5 h-5" />
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => onEdit(client)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={() => onDelete(client.id)}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {client.email && (
            <div className="flex items-center text-gray-600">
              <Mail className="w-4 h-4 mr-2 text-gray-400" />
              <a href={`mailto:${client.email}`} className="hover:text-blue-600">
                {client.email}
              </a>
            </div>
          )}
          {client.phone && (
            <div className="flex items-center text-gray-600">
              <Phone className="w-4 h-4 mr-2 text-gray-400" />
              <a href={`tel:${client.phone}`} className="hover:text-blue-600">
                {client.phone}
              </a>
            </div>
          )}
          {client.website && (
            <div className="flex items-center text-gray-600">
              <Globe className="w-4 h-4 mr-2 text-gray-400" />
              <a
                href={client.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-600 truncate"
              >
                {client.website.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center text-sm text-gray-600">
            <UserCheck className="w-4 h-4 mr-1" />
            <span>{client.contact_count || 0} contacts</span>
          </div>
          <button
            onClick={() => navigate(`/dashboard/clients/${client.id}`)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
}